/* eslint-disable max-lines-per-function */
import React from "react";
import { DialogContent, Grid, TextField, Typography } from "@material-ui/core";
import { useSnackbar } from "components/haven-design/snackbar";
import { SubmitButton } from "components/buttons";
import { Button, Modal } from "components/haven-design";
import logger from "services/logger";
import { DialogActions } from "components/haven-design/modal/dialog";
import { SelectAutocomplete } from "components/haven-design/form/select";
import { requestReinstatementVariables } from "types/gql-types";
import { ReinstateModalLoaderProps } from "./reinstate-modal-loader";

const reinstatementReasons = [
  "In Hospital",
  "In Jail",
  "Never received the message",
  "Out of the country",
  "Ill",
  "Other",
];

export type ReinstateModalProps = ReinstateModalLoaderProps & {
  interestRemovalReason?: string;
  loading?: boolean;
  requestReinstatement: (variables: {variables: requestReinstatementVariables}) => Promise<any>;
}


const ReinstateModal: React.FC<ReinstateModalProps> =
  (props) => {
    const { openSnackbar } = useSnackbar();
    const [selectedReason, setSelectedReason] = React.useState<string | null>(null);
    const [otherReason, setOtherReason] = React.useState("");
    const [reasonDetails, setReasonDetails] = React.useState("");

    const submitForm = () => {
      if (!selectedReason) {
        openSnackbar({ message: "Failed to send a request. Missing a reason", status: "error" });
        return;
      }
      props.requestReinstatement(
        { variables: {
          interestId: props.interestId,
          reason: selectedReason,
          other: otherReason,
          details: reasonDetails,
        } },
      )
        .then(() => {
          openSnackbar({ message: "Successfully requested reinstatement.", status: "success" });
          props.onRequestClose(true);
        })
        .catch((err) => {
          logger.sendError(err);
          openSnackbar({ message: "Failed to send a request", status: "error" });
        });
    };

    return (
      <Modal
        fullWidth
        title="Request Reinstatement"
        isOpen={props.isOpen}
        onRequestClose={() => props.onRequestClose(false)}>
        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="body1" component="p">
                You were removed from <strong>{props.interestListName}
                </strong> on {new Date(props.removalDate).toLocaleDateString()}{
                  props.interestRemovalReason ? (
                    ` due to the following reason: ${props.interestRemovalReason}`) : ""}. You can request reinstatement to the waiting list within {
                  props.reinstatementDays} days of being removed.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <SelectAutocomplete
                id="reinstatement-reason"
                required
                label="Reinstatement Reason"
                options={reinstatementReasons}
                value={selectedReason}
                onChange={(_, value) => setSelectedReason(value ?? "")}
              />
            </Grid>
            {selectedReason === "Other" && (
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  fullWidth
                  required={selectedReason === "Other"}
                  label="Other Reason"
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                />
              </Grid>)}
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                fullWidth
                label="Details (optional)"
                value={reasonDetails}
                onChange={(e) => setReasonDetails(e.target.value)}
                multiline
                maxRows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            color="secondary"
            size="S"
            onClick={() => props.onRequestClose(false)}
          >
            Cancel
          </Button>
          <SubmitButton
            text="Send request"
            loadingText="Sending"
            isLoading={props.loading}
            disabled={[null, ""].includes(selectedReason) || (selectedReason === "Other" && otherReason === "")}
            onClick={submitForm}
          />
        </DialogActions>
      </Modal>
    );
  };

export default ReinstateModal;
